import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';

import { getFirestore, provideFirestore } from '@angular/fire/firestore';

import { AngularFireModule } from '@angular/fire/compat';
import {
  AngularFireAuth,
  AngularFireAuthModule,
} from '@angular/fire/compat/auth';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from 'src/components/app/app.component';
import { environment } from 'src/environments/environment';
import { EditorComponent } from '../components/editor/editor.component';
import { EntriesComponent } from '../components/entries/entries.component';
import { SidebarComponent } from '../components/sidebar/sidebar.component';

import { AuthService } from '../services/auth/auth.service';
import { EntryService } from 'src/services/entry/entry.service';
import { FirestoreEntryServiceService } from '../services/entry/firestore-entry-service.service';
import { OfflineEntryServiceService } from '../services/entry/offline-entry-service.service';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { TagService } from 'src/services/tag/tag.service';
import { FirestoreTagService } from 'src/services/tag/firestore-tag.service';
import { OfflineTagService } from 'src/services/tag/offline-tag.service';
import { FirestoreUserService } from 'src/services/user/firestore-user.service';
import { OfflineUserService } from 'src/services/user/offline-user.service';
import { UserService } from 'src/services/user/user.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PrefundCurrencyPipe } from './prefund-currency.pipe';
import { getFunctions, provideFunctions } from '@angular/fire/functions';

export function initializeAngularApp(
  authService: AuthService
): () => Promise<void> {
  return () => authService.initializeAuth();
}

export function entryServiceFactory(authService: AuthService): EntryService {
  return authService.user
    ? new FirestoreEntryServiceService()
    : new OfflineEntryServiceService();
}

export function tagServiceFactory(authService: AuthService): TagService {
  return authService.user ? new FirestoreTagService() : new OfflineTagService();
}

export function userServiceFactory(authService: AuthService): UserService {
  return authService.user
    ? new FirestoreUserService()
    : new OfflineUserService();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    AngularFireAuthModule,
    BrowserAnimationsModule,
    PrefundCurrencyPipe,
    EditorComponent,
    SidebarComponent,
    EntriesComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // NoopAnimationsModule,
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions()),

    AngularFireAuth,
    { provide: FIREBASE_OPTIONS, useValue: environment.firebaseConfig },

    {
      provide: APP_INITIALIZER,
      useFactory: initializeAngularApp,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: EntryService,
      useFactory: entryServiceFactory,
      deps: [AuthService],
    },
    {
      provide: TagService,
      useFactory: tagServiceFactory,
      deps: [AuthService],
    },
    {
      provide: UserService,
      useFactory: userServiceFactory,
      deps: [AuthService],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 2500 } },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
